/**
 * Translation Hook
 * Fetch and use translations from backend
 */

import { useState, useEffect, useCallback, useContext, createContext } from 'react';

interface Translations {
  [key: string]: string | Translations;
}

interface TranslationContextType {
  language: string;
  setLanguage: (lang: string) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  isLoading: boolean;
  availableLanguages: LanguageInfo[];
}

interface LanguageInfo {
  code: string;
  name: string;
  flag: string;
}

// Create context
export const TranslationContext = createContext<TranslationContextType | null>(null);

// Storage keys
const STORAGE_KEY_LANG = 'miniapp_language';
const STORAGE_KEY_CACHE = 'miniapp_translations_';

// Default translations (English fallback)
const DEFAULT_TRANSLATIONS: Translations = {
  common: {
    loading: 'Loading...',
    loadingProducts: 'Loading products...',
    loadingProfile: 'Loading profile...',
    loadingWallet: 'Loading wallet...',
    error: 'Error',
    retry: 'Retry',
    cancel: 'Cancel',
    confirm: 'Confirm',
    save: 'Save',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    search: 'Search',
    searchProducts: 'Search products...',
    all: 'All',
    none: 'None',
    yes: 'Yes',
    no: 'No',
    continue: 'Continue',
    product: 'Product',
    products: 'Products',
    page: 'Page',
    of: 'of',
    loadMore: 'Load More',
    noResults: 'No results found',
    notFound: 'Not found',
    backToStore: 'Back to Store',
    add: 'Add',
    buyNow: 'Buy Now',
    addedToCart: 'Added to cart!',
    copy: 'Copy',
    copied: 'Copied!',
    orderSummary: 'Order Summary'
  },
  shop: {
    store: 'Store',
    products: 'Products',
    categories: 'Categories',
    allCategories: 'All Categories',
    noProducts: 'No products available',
    noProductsFor: 'No products found for "{query}"',
    noCategories: 'No categories available',
    storeClosed: 'Store is currently closed',
    storeUnavailable: 'Store Unavailable',
    storeUnavailableMessage: 'This store is currently not available. Please try again later.',
    addToCart: 'Add to Cart',
    outOfStock: 'Out of Stock',
    inStock: 'In Stock',
    backorder: 'Backorder',
    left: 'left',
    available: 'available',
    featured: 'Featured',
    productNotFound: 'Product not found',
    productTypes: {
      physical: 'Physical',
      downloadable: 'Download',
      keys: 'Key',
      subscription: 'Subscription'
    }
  },
  cart: {
    title: 'Shopping Cart',
    empty: 'Your cart is empty',
    emptyMessage: 'Browse products and add items to your cart',
    browseProducts: 'Browse Products',
    items: 'items',
    itemsInCart: 'items in cart',
    subtotal: 'Subtotal',
    shipping: 'Shipping',
    shippingCalculated: 'Shipping calculated at checkout',
    physicalAndDigital: 'Cart contains physical & digital items',
    digitalInstant: 'Digital items - instant delivery',
    total: 'Total',
    checkout: 'Proceed to Checkout',
    clear: 'Clear',
    clearCart: 'Clear all items from cart?',
    remove: 'Remove',
    quantity: 'Qty'
  },
  checkout: {
    title: 'Checkout',
    reviewOrder: 'Review Order',
    shipping: 'Shipping',
    payment: 'Payment',
    confirm: 'Confirm',
    orderItems: 'Order Items',
    savedAddresses: 'Saved Addresses',
    orEnterNew: 'or enter new address',
    shippingAddress: 'Shipping Address',
    shippingInfo: 'Shipping Information',
    shippingMethod: 'Shipping Method',
    deliveryAddress: 'Delivery Address',
    selectPaymentMethod: 'Select Payment Method',
    selectCoin: 'Select Cryptocurrency',
    paymentMethod: 'Payment Method',
    noPaymentMethods: 'No payment methods available',
    placeOrder: 'Place Order',
    processing: 'Processing...',
    digitalDelivery: 'Digital items will be delivered instantly after payment',
    calculatedNext: 'Calculated next',
    free: 'Free',
    discount: 'Discount',
    tax: 'Tax',
    orderNote: 'Order Note (optional)',
    addInstructions: 'Add any special instructions...',
    fullName: 'Full Name',
    phone: 'Phone Number',
    address: 'Address',
    addressLine1: 'Address Line 1',
    addressLine2: 'Address Line 2',
    addressOptional: 'Apt, Suite, Unit (optional)',
    city: 'City',
    state: 'State',
    postalCode: 'Postal Code',
    country: 'Country',
    // New keys for shipping address textarea
    addressHint: 'Enter your complete delivery address including name, street, city, and country',
    addressPlaceholder: 'Full Name\nStreet Address, Apt/Suite\nCity, State/Province ZIP\nCountry\nPhone Number',
    addressFormatHint: 'Include your name, complete address, postal code, country, and phone number',
    // New keys for shipping method selection
    selectShippingMethod: 'Select Shipping Method',
    noShippingMethods: 'No shipping methods available',
    standardShipping: 'Standard Shipping',
    expressShipping: 'Express Shipping',
    shippingDescription: 'Delivery estimates vary by carrier',
    // New keys for coupon
    couponCode: 'Coupon Code',
    enterCouponCode: 'Enter coupon code',
    apply: 'Apply',
    remove: 'Remove',
    discountApplied: 'off applied',
    couponApplied: 'Coupon applied!',
    invalidCoupon: 'Invalid coupon code',
    couponExpired: 'Coupon has expired',
    couponLimitReached: 'Coupon usage limit reached',
    couponMinOrder: 'Minimum order amount not met',
    couponAlreadyUsed: 'You have already used this coupon',
    // Summary labels
    subtotal: 'Subtotal',
    shippingFee: 'Shipping',
    totalAmount: 'Total',
    // Error messages
    failedToCreateOrder: 'Failed to create order',
    failedToInitPayment: 'Failed to initialize payment',
    failedToPlaceOrder: 'Failed to place order. Please try again.'
  },
  orders: {
    title: 'Orders',
    myOrders: 'My Orders',
    noOrders: 'No orders yet',
    orderNumber: 'Order #',
    orderDetails: 'Order Details',
    orderNotFound: 'Order not found',
    status: {
      pending: 'Pending',
      processing: 'Processing',
      shipped: 'Shipped',
      delivered: 'Delivered',
      completed: 'Completed',
      cancelled: 'Cancelled',
      refunded: 'Refunded'
    },
    paymentStatus: {
      pending: 'Awaiting Payment',
      processing: 'Processing',
      completed: 'Paid',
      failed: 'Failed',
      refunded: 'Refunded'
    },
    timeline: 'Order Timeline',
    tracking: 'Tracking Information',
    track: 'Track',
    needHelp: 'Need Help?',
    helpMessage: 'For help with your order, please contact the store through the bot chat.',
    completePayment: 'Complete Payment',
    sendTo: 'Send {amount} {currency} to:',
    paymentAutoConfirm: 'Payment will be confirmed automatically once detected',
    viewSubscription: 'View Subscription'
  },
  downloads: {
    title: 'Downloads',
    myDownloads: 'My Downloads',
    download: 'Download',
    noDownloads: 'No downloads yet',
    downloadsLeft: '{remaining}/{total} downloads left',
    clickToDownload: 'Click to download',
    expired: 'Expired',
    sentToChat: 'File sent to your Telegram chat!',
    downloadFailed: 'Failed to send file'
  },
  reviews: {
    title: 'Reviews',
    review_singular: 'review',
    reviews: 'reviews',
    product_reviews: 'Product Reviews',
    no_reviews: 'No reviews yet',
    load_more: 'Load more reviews',
    vendor_response: 'Vendor Response',
    featured_review: 'Featured Review',
    write_review: 'Write a Review',
    helpful: 'Helpful',
    verified_purchase: 'Verified Purchase'
  },
  keys: {
    title: 'Keys',
    myKeys: 'My Keys',
    noKeys: 'No product keys yet',
    revealKey: 'Reveal Key',
    reveal: 'Reveal',
    copy: 'Copy',
    copied: 'Copied!',
    copiedToClipboard: 'Copied to clipboard!'
  },
  subscriptions: {
    title: 'Subscriptions',
    subs: 'Subs',
    mySubscriptions: 'My Subscriptions',
    noSubscriptions: 'No subscriptions yet',
    active: 'Active',
    expired: 'Expired',
    cancelled: 'Cancelled',
    expiresOn: 'Expires:',
    daysLeft: 'days left',
    renew: 'Renew',
    renewSuccess: 'Subscription renewed successfully!',
    accessContent: 'Access Content'
  },
  wallet: {
    title: 'Wallet',
    wallet: 'Wallet',
    balance: 'Balance',
    availableBalance: 'Available Balance',
    points: 'points',
    addFunds: 'Add Funds',
    withdraw: 'Withdraw',
    topUp: 'Top Up',
    recentTransactions: 'Recent Transactions',
    transactions: 'Transactions',
    noTransactions: 'No transactions yet',
    orders: 'Orders',
    totalSpent: 'Total Spent',
    referrals: 'Referrals',
    loadingWallet: 'Loading wallet...',
    addFundsComingSoon: 'Add Funds feature coming soon!',
    withdrawComingSoon: 'Withdraw feature coming soon!',
    transactionTypes: {
      deposit: 'Deposit',
      withdrawal: 'Withdrawal',
      purchase: 'Purchase',
      refund: 'Refund',
      referralBonus: 'Referral Bonus',
      subscription: 'Subscription',
      pointsConversion: 'Points Conversion'
    },
    comingSoon: 'Coming soon!'
  },
  profile: {
    title: 'Profile',
    profile: 'Profile',
    verified: 'Verified',
    memberSince: 'Member Since',
    totalSpent: 'Total Spent',
    settings: 'Settings',
    language: 'Language',
    notifications: 'Notifications',
    referralLink: 'Referral Link',
    yourReferralLink: 'Your Referral Link',
    notAvailable: 'Not available',
    addresses: 'Saved Addresses',
    savedAddresses: 'Saved Addresses',
    noAddresses: 'No saved addresses yet',
    noSavedAddresses: 'No saved addresses yet',
    default: 'Default',
    accountInfo: 'Account Info',
    quickAccess: 'Quick Access',
    logout: 'Logout',
    licenseKeys: 'License Keys',
    myOrders: 'My Orders',
    orders: 'Orders',
    balance: 'Balance',
    points: 'Points',
    loadingProfile: 'Loading profile...'
  },
  dashboard: {
    title: 'Dashboard',
    orders: 'Orders',
    downloads: 'Downloads',
    keys: 'Keys',
    subs: 'Subs'
  },
  payment: {
    selectMethod: 'Select Payment Method',
    wallet: 'Wallet',
    walletBalance: 'Wallet Balance',
    card: 'Card',
    crypto: 'Crypto',
    cryptocurrency: 'Cryptocurrency',
    stars: 'Telegram Stars',
    mobile: 'Mobile',
    payWith: 'Pay with',
    amount: 'Amount',
    paymentAddress: 'Payment Address',
    sendExactly: 'Send exactly',
    paymentDetected: 'Payment will be detected automatically',
    insufficientFunds: 'Insufficient Funds',
    topUpWallet: 'Top Up Wallet',
    // Payment method descriptions
    starsDescription: 'Pay instantly with Telegram Stars',
    walletDescription: 'Pay from your store wallet',
    cryptoDescription: 'Pay with cryptocurrency',
    cardDescription: 'Pay with credit or debit card',
    mobileDescription: 'Pay with mobile money',
    customDescription: 'Pay using custom method',
    // Payment page translations
    paymentTitle: 'Payment',
    paymentSuccessful: 'Payment Successful',
    completePayment: 'Complete Your Payment',
    paymentCompleted: 'Payment Completed!',
    orderConfirmed: 'Your order has been confirmed and is being processed.',
    viewOrders: 'View My Orders',
    continueShopping: 'Continue Shopping',
    awaitingPayment: 'Awaiting Payment',
    amountToPay: 'Amount to Pay',
    scanToPay: 'Scan to pay {amount} {coin}',
    networkLabel: 'Network: {network}',
    sendToAddress: 'Send exactly {amount} {coin} to this address',
    addressCopied: 'Address copied to clipboard!',
    copyAddress: 'Copy Address',
    copied: 'Copied!',
    // Payment type titles
    cryptoPaymentTitle: 'Cryptocurrency Payment',
    walletPaymentTitle: 'Wallet Payment',
    starsPaymentTitle: 'Telegram Stars Payment',
    customPaymentTitle: 'Payment',
    // Crypto instructions
    importantInstructions: 'Important Instructions',
    cryptoInstruction1: 'Send exactly {amount} {coin}',
    cryptoInstruction2: 'Send only {coin} to this address',
    cryptoInstruction2Network: 'Send only {coin} to this address ({network} network)',
    cryptoInstruction3: 'Payment will be confirmed after network verification',
    cryptoInstruction4: 'Do not close this page until payment is complete',
    // Telegram Stars payment
    starsAmount: 'Pay {amount} Stars',
    starsInvoiceSent: 'A payment invoice has been sent to your chat with the bot.',
    starsInstruction1: 'Go back to the bot chat',
    starsInstruction2: 'Tap the "Pay" button on the invoice',
    starsInstruction3: 'Complete the payment with Telegram Stars',
    // Custom payment
    customPaymentInstructions: 'Payment Instructions',
    openPaymentLink: 'Open Payment Link',
    scanToPayCustom: 'Scan to pay',
    // Order info section
    orderNumberLabel: 'Order Number',
    paymentMethodLabel: 'Payment Method',
    statusLabel: 'Status',
    usdEquivalent: '≈ {amount} USD',
    // Refresh payment status
    refreshStatus: 'Refresh Payment Status',
    refreshing: 'Checking...',
    statusUpdated: 'Payment status updated',
    noStatusChange: 'Payment still pending',
    // Payment evidence submission
    ivePaid: "I've Paid",
    submitEvidence: 'Submit Payment Evidence',
    submitEvidenceDescription: 'Please provide proof of your payment for verification. You can upload a screenshot, enter a transaction ID, or provide a transaction URL.',
    screenshot: 'Screenshot/PDF',
    transactionId: 'Transaction ID',
    transactionUrl: 'Transaction URL',
    uploadScreenshot: 'Tap to upload screenshot or PDF',
    uploadHint: 'JPEG, PNG, GIF, WebP or PDF (max 10MB)',
    transactionIdPlaceholder: 'Enter your transaction ID...',
    transactionUrlPlaceholder: 'https://...',
    submitEvidenceBtn: 'Submit Evidence',
    submitting: 'Submitting...',
    evidenceSubmitted: 'Payment evidence submitted successfully!',
    evidenceSubmittedTitle: 'Evidence Submitted',
    awaitingConfirmation: 'Awaiting Confirmation',
    evidenceReceivedText: 'Your payment evidence has been received and is being reviewed by the vendor. You will be notified once your payment is confirmed.',
    whatHappensNext: 'What Happens Next',
    confirmationStep1: 'The vendor will review your payment evidence',
    confirmationStep2: 'Once confirmed, your order will be processed',
    confirmationStep3: 'You will receive a notification when payment is confirmed',
    checkStatus: 'Check Status',
    paid: 'Paid',
    amountLabel: 'Amount',
    // Evidence validation messages
    selectEvidenceType: 'Please select an evidence type',
    uploadFile: 'Please upload a file',
    enterEvidence: 'Please enter your evidence',
    invalidUrl: 'Please enter a valid URL starting with http:// or https://',
    invalidFileType: 'Invalid file type. Please upload an image or PDF.',
    fileTooLarge: 'File is too large. Maximum size is 10MB.',
    submitError: 'Failed to submit evidence. Please try again.'
  },
  variation: {
    color: 'Color',
    size: 'Size',
    options: 'Options',
    bulk_deals: 'Bulk Deals',
    quantity_discount: 'Quantity Discount',
    save: 'Save',
    more_colors: 'More Colors',
    more_sizes: 'More Sizes',
    available: 'available',
    selected: 'Selected',
    bulk_tier: 'Bulk Tier',
    buy_more_save_more: 'Buy more, save more!',
    minimum_qty: 'Minimum',
    discount: 'Discount',
    price_per_unit: 'Per unit',
    total: 'Total',
    please_select_color_first: 'Please select a color first',
    please_select_size_first: 'Please select a size first',
    please_select_color: 'Please select a color',
    please_select_size: 'Please select a size',
    please_select_option: 'Please select an option',
    out_of_stock: 'Out of stock',
    only_x_left: 'Only {count} left',
    bulk_price: 'Bulk Price',
    per_unit: 'per unit',
    units: 'units',
    unit: 'unit',
    each: 'each',
    bulk_pricing_available: 'Bulk Pricing Available',
    select_color: 'Select Color',
    choose_preferred_color: 'Choose your preferred color:',
    color_in_stock: 'in stock',
    color_out_of_stock: 'Out of stock',
    select_size: 'Select Size',
    choose_your_size: 'Choose your size:'
  }
};

/**
 * Custom hook for translations
 */
export function useTranslation() {
  const context = useContext(TranslationContext);

  if (!context) {
    // Fallback when used outside provider
    return {
      language: 'en',
      setLanguage: () => {},
      t: (key: string) => getNestedValue(DEFAULT_TRANSLATIONS, key) || key,
      isLoading: false,
      availableLanguages: [{ code: 'en', name: 'English', flag: '🇺🇸' }]
    };
  }

  return context;
}

/**
 * Get nested value from object by dot-notation key
 */
function getNestedValue(obj: Translations, key: string): string | undefined {
  const keys = key.split('.');
  let result: any = obj;

  for (const k of keys) {
    if (result && typeof result === 'object' && k in result) {
      result = result[k];
    } else {
      return undefined;
    }
  }

  return typeof result === 'string' ? result : undefined;
}

/**
 * Translation Provider hook for context
 */
export function useTranslationProvider(botUsername: string, defaultLang?: string) {
  const [language, setLanguageState] = useState<string>(() => {
    // Priority: localStorage > Telegram user language > default > 'en'
    const stored = localStorage.getItem(STORAGE_KEY_LANG);
    if (stored) return stored;

    const tgLang = window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code;
    if (tgLang) return tgLang;

    return defaultLang || 'en';
  });

  // Store botUsername for API calls
  const [storedBotUsername] = useState(botUsername);

  const [translations, setTranslations] = useState<Translations>(DEFAULT_TRANSLATIONS);
  const [isLoading, setIsLoading] = useState(false);
  const [availableLanguages, setAvailableLanguages] = useState<LanguageInfo[]>([
    { code: 'en', name: 'English', flag: '🇬🇧' }
  ]);

  const baseUrl = process.env.REACT_APP_API_URL || '';

  // Sync with defaultLang when it changes (e.g., from database fetch)
  // This ensures that when ShopLayout fetches language from database,
  // the translation context updates accordingly
  useEffect(() => {
    if (defaultLang && defaultLang !== language) {
      console.log('Syncing language from database:', defaultLang);
      setLanguageState(defaultLang);
      localStorage.setItem(STORAGE_KEY_LANG, defaultLang);
    }
  }, [defaultLang]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load available languages from API
  useEffect(() => {
    const loadLanguages = async () => {
      try {
        const res = await fetch(`${baseUrl}/api/translations/languages`);
        const data = await res.json();
        if (data.success && Array.isArray(data.data) && data.data.length > 0) {
          setAvailableLanguages(data.data);
        }
      } catch (error) {
        // Keep default English on error
      }
    };
    loadLanguages();
  }, [baseUrl]);

  // Load translations for current language
  const loadTranslations = useCallback(async (lang: string, forceRefresh: boolean = false) => {
    // Check cache first (5 minute cache)
    const cacheKey = STORAGE_KEY_CACHE + lang;
    const cached = localStorage.getItem(cacheKey);

    if (!forceRefresh && cached) {
      try {
        const parsed = JSON.parse(cached);
        // Validate cache has all required modules
        const requiredModules = ['profile', 'dashboard', 'shop', 'common'];
        const hasAllModules = requiredModules.every(m => parsed.data && parsed.data[m]);
        if (parsed.timestamp && Date.now() - parsed.timestamp < 300000 && hasAllModules) { // 5 minute cache
          setTranslations(parsed.data);
          return;
        }
        // Invalid cache, remove it
        localStorage.removeItem(cacheKey);
      } catch (e) {
        localStorage.removeItem(cacheKey);
      }
    }

    setIsLoading(true);

    try {
      // Load all translation modules
      const modules = ['common', 'shop', 'cart', 'checkout', 'orders', 'downloads', 'keys', 'subscriptions', 'wallet', 'profile', 'dashboard', 'payment', 'variation', 'reviews'];

      const loadedTranslations: Translations = { ...DEFAULT_TRANSLATIONS };

      await Promise.all(
        modules.map(async (module) => {
          try {
            const res = await fetch(`${baseUrl}/api/translations/${lang}/${module}`);
            const data = await res.json();
            if (data.success && data.data) {
              loadedTranslations[module] = {
                ...DEFAULT_TRANSLATIONS[module] as Translations,
                ...data.data
              };
            }
          } catch (e) {
            // Keep default for this module
          }
        })
      );

      setTranslations(loadedTranslations);

      // Cache translations
      localStorage.setItem(cacheKey, JSON.stringify({
        timestamp: Date.now(),
        data: loadedTranslations
      }));
    } catch (error) {
      console.error('Error loading translations:', error);
      // Keep using defaults
    } finally {
      setIsLoading(false);
    }
  }, [baseUrl]);

  // Load translations when language changes
  useEffect(() => {
    loadTranslations(language);
  }, [language, loadTranslations]);

  // Set language and persist (both localStorage and database)
  const setLanguage = useCallback(async (lang: string) => {
    localStorage.setItem(STORAGE_KEY_LANG, lang);
    // Clear ALL translation caches to force fresh fetch
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_CACHE)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    setLanguageState(lang);
    // Force load translations for new language
    loadTranslations(lang, true);

    // Also save to database via API to sync with native bot
    try {
      const token = localStorage.getItem('auth_token');
      if (token && storedBotUsername) {
        const response = await fetch(`${baseUrl}/api/shop/${storedBotUsername}/customer/language`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ language: lang })
        });

        if (!response.ok) {
          console.warn('Failed to sync language to database:', response.status);
        } else {
          console.log('Language synced to database:', lang);
        }
      } else {
        console.warn('Cannot sync language: missing token or botUsername', { hasToken: !!token, botUsername: storedBotUsername });
      }
    } catch (e) {
      console.warn('Error syncing language to database:', e);
    }
  }, [baseUrl, storedBotUsername, loadTranslations]);

  // Translation function with parameter interpolation
  const t = useCallback((key: string, params?: Record<string, string | number>): string => {
    let value = getNestedValue(translations, key);

    // Fallback to default translations
    if (!value) {
      value = getNestedValue(DEFAULT_TRANSLATIONS, key);
    }

    // Final fallback to key itself
    if (!value) {
      return key;
    }

    // Interpolate parameters
    if (params) {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        value = value!.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(paramValue));
      });
    }

    return value;
  }, [translations]);

  return {
    language,
    setLanguage,
    t,
    isLoading,
    availableLanguages
  };
}

export default useTranslation;
